import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useUserProfile } from "@vega-api/extended/auth/hooks";
import {
  useStartEmailVerification,
  validateEmailVerificationPin,
} from "@vega-api/generated/auth-email-verification-v1/auth-email-verification-v1";
import { useUpdateUser } from "@vega-api/generated/user-management-v1/user-management-v1";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { OtpField } from "@vega-core/components/Form/Controls/OtpField/OtpField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

enum Steps {
  Email = 1,
  Verification = 2,
}

export const EditActionModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
}) => {
  const { data: profile, refetch } = useUserProfile();
  const [error, setError] = useState<string>();
  const toast = useToast();

  const { mutate: startEmailVerification, isPending } =
    useStartEmailVerification({
      mutation: {
        onSuccess: (data, { params: { email } }) => {
          setEmail(email);
          setStep(Steps.Verification);
        },
        onError: (error: unknown) => {
          const { description } = getErrorMessage(error as ErrorType<unknown>, {
            defaultMessage: m.error_failedToSendVerification(),
          });
          setError(description);
        },
      },
    });

  const [email, setEmail] = useState(profile?.secondaryContactEmail || "");
  const [step, setStep] = useState<Steps>(Steps.Email);
  const [otp, setOtp] = useState("");

  const reset = () => {
    setStep(Steps.Email);
    setEmail(profile?.secondaryContactEmail || "");
    setOtp("");
  };

  const hasSecondaryContactEmail = Boolean(profile?.secondaryContactEmail);

  const { mutate: updateUser } = useUpdateUser({
    mutation: {
      onSuccess: () => {
        setOpen(false);
        reset();
        refetch();
      },
      onError: (error: unknown) => {
        const { description } = getErrorMessage(error as ErrorType<unknown>, {
          defaultMessage: m.error_failedToUpdateContact(),
        });
        toast({
          title: m.error_failedToUpdateContact(),
          description,
          variant: "destructive",
        });
      },
    },
  });

  const emailFormik = useFormik({
    initialValues: { email: email },
    onSubmit(values: { email: string }) {
      startEmailVerification({ params: { email: values.email } });
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(m.formValidation_invalid({ name: m.formFields_email() }))
        .test(
          "notEqual",
          m.form_emailCannotBeTheSame(),
          (value) => value !== profile?.email,
        )
        .required(
          m.formValidation_required({
            name: m.formFields_email(),
          }),
        ),
    }),
  });

  const optLength = 4;
  const onChange = async (value: string) => {
    setOtp(value);

    if (value.length === optLength) {
      try {
        await validateEmailVerificationPin({
          code: Number(value),
          email: email,
        });

        setOpen(false);
        reset();
        refetch();
      } catch (error: unknown) {
        const { description } = getErrorMessage(error as ErrorType<unknown>, {
          defaultMessage: m.error_failedToSendVerification(),
        });
        setError(description);
      }
    }
  };

  const stepContent = () => {
    switch (step) {
      case Steps.Verification:
        return (
          <div className="flex flex-col text-center py-9 gap-4">
            <OtpField onChange={onChange} value={otp} valueLength={optLength} />
            {isTruthy(error) ? (
              <Notice content={error} type={NoticeType.ERROR} />
            ) : null}
          </div>
        );
      case Steps.Email:
      default:
        return (
          <div className="pt-9 flex flex-col gap-4">
            <form onSubmit={emailFormik.handleSubmit}>
              <InputControl
                error={
                  emailFormik.errors.email && emailFormik.touched.email
                    ? emailFormik.errors.email
                    : ""
                }
                label={m.formFields_email()}
                name="email"
                onBlur={emailFormik.handleBlur}
                onChange={emailFormik.handleChange}
                placeholder={m.placeholders_email()}
                type="email"
                value={emailFormik.values.email}
                withBorder
              />
            </form>
            <Notice
              content={
                isTruthy(error) ? error : m.settings_associatedUsersWillBe()
              }
              type={isTruthy(error) ? NoticeType.ERROR : undefined}
            />
          </div>
        );
    }
  };
  const [countdown, setCountdown] = useState(60);
  const countdownId = useRef<number | null>(null);

  useEffect(() => {
    if (step === Steps.Verification) {
      countdownId.current = window.setInterval(() => {
        if (countdown > 0) {
          setCountdown((prevCount) => prevCount - 1);
        } else {
          window.clearInterval(countdownId.current as number);
        }
      }, 1000);
    }

    return () => {
      if (countdownId.current) {
        window.clearInterval(countdownId.current);
      }
    };
  }, [step]);

  const resetCountdown = () => {
    startEmailVerification({ params: { email: email } });

    setCountdown(60);

    if (countdownId.current) {
      window.clearInterval(countdownId.current);
    }
    countdownId.current = window.setInterval(() => {
      setCountdown((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
    }, 1000);
  };

  const handleClose = (value: boolean) => {
    setOpen(value);
    setEmail("");

    if (!value) {
      reset();
    }
  };

  const getModalProps = () => {
    switch (step) {
      case Steps.Verification:
        return {
          ModalButtonsGroupProps: {
            mainAction: resetCountdown,
            mainActionText:
              countdown > 0
                ? m.btn_resendCodeIn({ count: countdown })
                : m.btn_resend(),
            disabled: countdown > 0,
            secondaryAction: () => {
              handleClose(false);
            },
            secondaryActionText: m.btn_cancel(),
          },
          open: open,
          panelClassName: "w-[31.25rem]",
          setOpen: handleClose,
          title: m.settings_verifyYourEmailAddress(),
          description: m.settings_weveSentACodeToEmail({ email }),
        };
      case Steps.Email:
      default:
        return {
          ModalButtonsGroupProps: {
            mainAction: emailFormik.handleSubmit,
            mainActionText: m.btn_next(),
            disabled: !emailFormik.isValid || isPending,
            secondaryAction: () => {
              handleClose(false);
            },

            secondaryActionText: m.btn_cancel(),
            thirdActionElement: hasSecondaryContactEmail ? (
              <ThemedButton
                dataTestId="delete-button"
                onClick={() => {
                  updateUser({
                    userId: profile?.id || "",
                    data: { secondaryContactEmail: "" },
                  });
                }}
                variant="destructive"
              >
                {m.btn_delete()}
              </ThemedButton>
            ) : undefined,
          },
          open: open,
          panelClassName: "w-[500px]",
          setOpen: handleClose,
          title: hasSecondaryContactEmail
            ? m.form_editEmail()
            : m.settings_addEmail(),
        };
    }
  };

  return (
    <ActionaryModal data-testid="edit-action-modal" {...getModalProps()}>
      {stepContent()}
    </ActionaryModal>
  );
};
